import { GalleryVerticalEnd, LifeBuoy, LogOut, User } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { OphelosIcon } from '@/components/branding/ophelos-icon';
import { DebtGroup } from '@/components/debt/DebtGroup';
import { RecentPayments } from './_partials/_dashboardPartials/RecentPayments';
import { Layout } from '@/components/layout/Layout';
import { useTheme } from '@/components/layout/ThemeProvider';
import { SettingsMenu } from '@/components/ui/SettingsMenu';
import { CurrencyDisplay } from '@/components/ui/CurrencyDisplay';
import { type BannerState, bannerState, StatusBanner } from '@/components/ui/StatusBanner';
import { useConfiguration } from '@/contexts/ConfigurationContext';
import { useDebtData } from '@/contexts/DebtDataContext';
import { DashboardErrorPage } from '@/pages/_partials/DashboardErrorPage';
import { useAuth } from '@/contexts/customer-auth-context';
import { usePollForSession } from '@/hooks/usePollForSession';
import { usePreviewNavigate } from '@/hooks/usePreviewNavigate';
import { useTranslation } from '@/hooks/useTranslation';
import { Loader } from '@ophelos/ui/components/loader';
import { ACCOUNT_ENTRY_ID, accountPaymentPath, buildAccountEntry, coveredByAccountPayment } from '@/lib/accountPayment';
import { accountPaymentSurface } from '@/lib/accountPaymentOffer';
import { balancesByCurrency, isAccountPaySnapshotDebt, isClearedDebt, isMultiCurrencyAccount, isUnpaidDebt } from '@/lib/debtFilters';
import { debtPage } from '@/lib/debtNavigation';
import { activeAccountPayment, isScheduledPayment } from '@/lib/paymentPlans';
import { updatePaymentSessionStatusOnConnect } from '@/lib/connect-api';
import type { DebtData } from '@/types';
import { Button } from '@ophelos/ui/components/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@ophelos/ui/components/tooltip';

const PAYMENT_BANNER_COPY: Record<BannerState, string> = {
  pending: 'payment.successBanner.pending',
  notice: 'payment.successBanner.timeout',
  success: 'payment.successBanner.description',
};

const CANCELLED_BANNER_COPY = {
  plan: { title: 'paymentPlan.cancelledBanner.title', description: 'paymentPlan.cancelledBanner.description' },
  scheduled: { title: 'payment.scheduledPanel.cancelledTitle', description: 'payment.scheduledPanel.cancelledDescription' },
};

/**
 * A future-dated single payment is a one-installment plan, so it shares the plan banner
 * with its own wording. A timeout replaces the description only — the title still names
 * what was created.
 */
function planBannerCopy(state: BannerState, isScheduled: boolean): { title: string; description: string } {
  const title = !isScheduled
    ? 'payment.planSuccessBanner.title'
    : state === 'pending'
      ? 'payment.scheduledPanel.confirmingTitle'
      : 'payment.successBanner.scheduledTitle';

  if (state === 'notice') return { title, description: 'payment.planSuccessBanner.timeout' };
  if (state === 'pending') {
    return { title, description: isScheduled ? 'payment.scheduledPanel.confirming' : 'payment.planSuccessBanner.pending' };
  }
  return { title, description: isScheduled ? 'payment.successBanner.scheduledDescription' : 'payment.planSuccessBanner.description' };
}

export default function Dashboard() {
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const [paymentSavings, setPaymentSavings] = useState<number | null>(null);
  // Set for a future-dated payment: the banner reads "scheduled for {date}" instead of "received".
  const [scheduledDate, setScheduledDate] = useState<string | null>(null);
  const [showPlanSuccess, setShowPlanSuccess] = useState(false);
  const [planSessionId, setPlanSessionId] = useState<string | null>(null);
  // Session behind an immediate card/SEPA payment, polled the same way as a plan.
  const [paymentSessionId, setPaymentSessionId] = useState<string | null>(null);
  // Cases the plan / the payment covers: 'case_xxx' for one, anything else account-wide.
  const [planScopeFromUrl, setPlanScopeFromUrl] = useState<string | null>(null);
  const [paidScopeFromUrl, setPaidScopeFromUrl] = useState<string | null>(null);
  // Which cancellation banner to show (null = none). One state drives both
  // visibility and copy for plan vs scheduled-payment cancellation.
  const [cancelledBanner, setCancelledBanner] = useState<'plan' | 'scheduled' | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const navigate = usePreviewNavigate();
  const { theme } = useTheme();
  const { accountId, signOut, authStage, getAccessToken } = useAuth();
  const { config } = useConfiguration();
  const { debts, accountPaymentPlan, accountNet, isLoading, error, refreshDebts } = useDebtData();

  // Get logo icon URL based on current theme
  const logoIcon = config.ui.branding.logoIcon;
  const logoIconUrl = theme === 'dark' ? logoIcon?.dark : logoIcon?.light;

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!accountId) {
      navigate('/login');
    }
  }, [accountId, navigate]);

  // Detect payment success from URL parameters and refresh data
  useEffect(() => {
    // ?payment_success={caseId|account}. A pre-collapse URL still in flight reads 'true',
    // which casesInScope takes as account-wide — banner shows, poll still lands.
    const paidScope = searchParams.get('payment_success');
    if (paidScope) {
      setShowPaymentSuccess(true);
      // Parse `saved` if the payment surface forwarded discount savings — used
      // to call out the discount in the success banner.
      const savedRaw = searchParams.get('saved');
      const savedNum = savedRaw != null ? Number.parseFloat(savedRaw) : NaN;
      setPaymentSavings(Number.isFinite(savedNum) && savedNum > 0 ? savedNum : null);
      setPaidScopeFromUrl(paidScope);
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('payment_success');
      newParams.delete('saved');
      setSearchParams(newParams, { replace: true });
      // Card/SEPA returns carry a session at both scopes (the server mints one for
      // account-level checkouts too), so the poll below owns the refresh — refetching
      // now would only read the balance before the payment is persisted. A return with
      // no session (nothing to poll) keeps the single refetch.
      if (!searchParams.get('session_id')) {
        refreshDebts();
      }
    }
  }, [searchParams, setSearchParams, refreshDebts]);

  // Consolidated URL param handling for redirects back to dashboard.
  // A single effect avoids stale-snapshot races between multiple setSearchParams calls.
  useEffect(() => {
    const planSuccess = searchParams.get('plan_success');
    const sessionId = searchParams.get('session_id');
    const planCancelled = searchParams.get('plan_cancelled');
    const scheduledPaymentCancelled = searchParams.get('scheduled_cancelled');

    if (!planSuccess && !sessionId && !planCancelled && !scheduledPaymentCancelled) return;

    const newParams = new URLSearchParams(searchParams);
    let dirty = false;

    // Plan creation: ?plan_success={caseId|account}&session_id={psess_xxx}
    // A single future-dated payment also lands here (it is a one-installment
    // plan) and carries ?scheduled={date} so the banner reads "scheduled".
    if (planSuccess) {
      setShowPlanSuccess(true);
      setPlanScopeFromUrl(planSuccess);
      setPlanSessionId(sessionId);
      setScheduledDate(searchParams.get('scheduled'));
      newParams.delete('plan_success');
      newParams.delete('session_id');
      newParams.delete('scheduled');
      dirty = true;
      // A multibanco plan returns inline rather than through checkout, so nothing
      // appends a session to poll. Refetch once instead of leaving the plan invisible
      // until the customer reloads — same fallback the immediate-payment path takes.
      if (!sessionId) {
        refreshDebts();
      }
    }

    // Plan cancellation: ?plan_cancelled=true
    if (planCancelled === 'true') {
      setCancelledBanner('plan');
      newParams.delete('plan_cancelled');
      dirty = true;
      refreshDebts();
    }

    // Scheduled single-payment cancellation: ?scheduled_cancelled=true
    if (scheduledPaymentCancelled === 'true') {
      setCancelledBanner('scheduled');
      newParams.delete('scheduled_cancelled');
      dirty = true;
      refreshDebts();
    }

    // Session-only redirect (immediate payment return from Ophelos): ?session_id={psess_xxx}
    // Card/SEPA only — Multibanco renders a reference in place and never redirects back.
    if (sessionId?.startsWith('psess_') && !planSuccess) {
      const sid = sessionId;
      setPaymentSessionId(sid);
      newParams.delete('session_id');
      dirty = true;

      (async () => {
        try {
          await updatePaymentSessionStatusOnConnect(sid, 'processing', authStage, getAccessToken);
        } catch (err) {
          console.error('[Dashboard] Failed to update session status:', { session_id: sid, error: err });
        }
      })();
    }

    if (dirty) {
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams, refreshDebts, authStage, getAccessToken]);

  const { isPending: isPlanPending, isTimedOut: isPlanTimedOut } = usePollForSession({
    enabled: showPlanSuccess,
    sessionId: planSessionId,
    onSessionComplete: refreshDebts,
    getAccessToken,
  });

  // Immediate payments wait on their own session the same way, so the balance the
  // customer lands on is the one the webhook wrote.
  const { isPending: isPaymentPending, isTimedOut: isPaymentTimedOut } = usePollForSession({
    enabled: showPaymentSuccess,
    sessionId: paymentSessionId,
    onSessionComplete: refreshDebts,
    getAccessToken,
  });

  // Cards held pending (dimmed, spinner, where the customer left them) until the poll lands.
  // Node-based, so a case whose balance the payment already settled is still in the set.
  const pendingCaseIds = useMemo(() => {
    const casesInScope = (scope: string) => (scope.startsWith('case_') ? [scope] : debts.filter(isUnpaidDebt).map((d) => d.id));

    const pendingScopes = [isPlanPending ? planScopeFromUrl : null, isPaymentPending ? paidScopeFromUrl : null].filter((scope) => scope != null);

    const ids = new Set(pendingScopes.flatMap(casesInScope));
    return ids.size > 0 ? [...ids] : null;
  }, [isPlanPending, planScopeFromUrl, isPaymentPending, paidScopeFromUrl, debts]);

  // One account-wide charge or plan renders as a single entry, not once per case
  // it covers.
  const accountEntry = useMemo<DebtData | null>(
    () => buildAccountEntry(accountPaymentPlan, debts, (count) => t('dashboard.coversDebts', { count })),
    [accountPaymentPlan, debts, t],
  );
  const accountPayment = activeAccountPayment(accountPaymentPlan);
  // A debt the account payment covers: grouped beside its entry and opened on its page.
  const isAccountPaymentMember = (d: DebtData) => coveredByAccountPayment(d, accountPayment);

  // Memoize grouped debts - must be before any conditional returns to satisfy hooks rules.
  // Groups are mutually exclusive: completed wins over scheduled/inProgress, so a
  // debt appears in at most one group.
  // - unpaid: live journey with nothing holding the debt, neither its own plan nor an account payment
  // - scheduled: single scheduled payments (per-case, plus a scheduled account entry and its debts)
  // - inProgress: an active payment plan (plus an account plan entry and its debts)
  // - completed: CUSTOMER_RESOLVED, RESOLVED, WITHDRAWN, RETURNED
  const groupedDebts = useMemo(() => {
    // Plan membership, not the node, decides the group — a future-dated plan is still at
    // an outreach node, so gating on PAYING would drop it from every group.
    const hasActivePlan = (d: DebtData) => Boolean(d.paymentPlan);
    // A pending case keeps its pre-payment group, so a settled balance doesn't move the
    // card to Cleared while the spinner is still on it.
    const isCompleted = (d: DebtData) => !pendingCaseIds?.includes(d.id) && isClearedDebt(d);

    // A scheduled single payment is a payment in progress, not a payment plan
    // and not an unpaid debt — it gets its own group.
    const scheduled = (d: DebtData) => isScheduledPayment(d.paymentPlan);
    // A member sits beside the account entry, as a debt with its own plan sits in that plan's
    // group. `paymentPlan` is case-scoped, so isUnpaidDebt alone would call it unpaid.
    const accountEntryIn = (group: typeof accountPayment) =>
      accountEntry && accountPayment === group ? [accountEntry, ...debts.filter((d) => isAccountPaymentMember(d) && !isCompleted(d))] : [];

    return {
      scheduled: [...accountEntryIn('scheduled'), ...debts.filter((d) => scheduled(d) && !isCompleted(d) && !isAccountPaymentMember(d))],
      unpaid: debts.filter((d) => isUnpaidDebt(d) && !isCompleted(d) && !isAccountPaymentMember(d)),
      inProgress: [
        ...accountEntryIn('plan'),
        ...debts.filter((d) => hasActivePlan(d) && !scheduled(d) && !isCompleted(d) && !isAccountPaymentMember(d)),
      ],
      completed: debts.filter(isCompleted),
    };
  }, [debts, accountEntry, accountPayment, pendingCaseIds]);

  // Show loading state while data is being fetched or not authenticated
  if (!accountId || isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <Loader fullScreen />
        </div>
      </Layout>
    );
  }

  // Block dashboard access on API failure — prevent showing €0.00 as if data loaded
  if (error) {
    return <DashboardErrorPage onRetry={refreshDebts} />;
  }

  // Payable-only: a zero-balance second currency must not invent multi-currency hero mode.
  const currencyTotals = balancesByCurrency(debts.filter((d) => d.currentBalance > 0));
  const multiCurrency = isMultiCurrencyAccount(debts.filter(isAccountPaySnapshotDebt));
  // Single-currency accounts keep one hero total; mixed never silently cross-sum.
  // The server's net is authoritative when it computed one — it is the same figure the
  // Multibanco code binds, and a locally re-derived total could disagree with it.
  const totalBalance = currencyTotals.length === 1 ? (accountNet?.netPayable ?? currencyTotals[0]!.total) : 0;
  // An account never spans more than one currency, so any debt names it. Fall back to the
  // first debt when nothing is payable, so a fully-settled account renders its own currency
  // rather than CurrencyDisplay's EUR default.
  const heroCurrency = currencyTotals[0]?.currency ?? debts[0]?.currency;
  // The paid case's own currency, not the hero's — a multi-currency account can clear one
  // currency's case while a different currency remains payable, and the savings banner must
  // still name the currency of the discount actually granted.
  const paidCase = paidScopeFromUrl?.startsWith('case_') ? debts.find((d) => d.id === paidScopeFromUrl) : undefined;
  const savingsCurrency = paidCase?.currency ?? heroCurrency;

  const handleDebtClick = (debt: DebtData) => {
    if (debt.id === ACCOUNT_ENTRY_ID) {
      if (accountPayment) navigate(accountPaymentPath(accountPayment));
      return;
    }
    const page = debtPage(debt, accountPayment);
    if (page) navigate(page.path);
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  // An arranged case keeps the CTA, and the account page explains the block — better than
  // the button vanishing unexplained.
  const accountSurface = accountPaymentSurface({ debts, accountPaymentPlan });
  const showAccountPayment = accountSurface.payAll;
  // Why Pay all is disabled: the same reason the account page would show, said up front.
  const payAllBlockedReason =
    accountSurface.surface === 'blocked'
      ? t(
          accountSurface.blockedBy === 'scheduled_account_payment'
            ? 'payment.accountScheduledBlock.description'
            : accountSurface.blockedBy === 'account_plan'
              ? 'payment.accountPlanBlock.description'
              : 'payment.casePlanBlock.description',
        )
      : null;

  const planState = bannerState(isPlanPending, isPlanTimedOut);
  const planCopy = planBannerCopy(planState, scheduledDate != null);
  const paymentState = bannerState(isPaymentPending, isPaymentTimedOut);

  return (
    <Layout>
      {showPaymentSuccess && (
        <StatusBanner state={paymentState} title={t('payment.successBanner.title')} description={t(PAYMENT_BANNER_COPY[paymentState])}>
          {paymentSavings != null && (
            <p className="text-sm font-medium">
              <Trans
                i18nKey="discount.savedWithOffer"
                components={{
                  amount: <CurrencyDisplay amount={paymentSavings} currency={savingsCurrency} className="font-semibold inline-flex gap-0" />,
                }}
              />
            </p>
          )}
        </StatusBanner>
      )}
      {showPlanSuccess && (
        <StatusBanner
          state={planState}
          title={t(planCopy.title)}
          description={t(planCopy.description, {
            date: scheduledDate ? new Date(`${scheduledDate}T00:00:00`).toLocaleDateString() : '',
          })}
        />
      )}
      {cancelledBanner && (
        <StatusBanner
          state="notice"
          title={t(CANCELLED_BANNER_COPY[cancelledBanner].title)}
          description={t(CANCELLED_BANNER_COPY[cancelledBanner].description)}
        />
      )}
      <div className="relative max-w-5xl w-full mx-auto">
        {/* Header */}
        <div className="relative p-4 flex items-center gap-2">
          <SettingsMenu size="icon-lg" className="md:order-2 md:ml-auto" />
          <div className="max-md:absolute max-md:left-1/2 max-md:top-1/2 max-md:-translate-x-1/2 max-md:-translate-y-1/2 md:order-1">
            {logoIconUrl ? (
              <img src={logoIconUrl} alt="Logo" className="h-8 max-w-24 w-auto object-contain opacity-60" />
            ) : (
              <OphelosIcon className="h-8 w-auto text-muted-foreground/25" />
            )}
          </div>
        </div>

        {/* Main content */}
        <div className="stack gap-10 w-full max-w-lg mx-auto px-4 pb-12 relative">
          {/* Balance card */}
          <div className="stack gap-1 text-center pt-6">
            {multiCurrency ? (
              <div className="space-y-1">
                {currencyTotals.map(({ currency, total }) => (
                  <h1 key={currency} className="font-bold tracking-tight">
                    <CurrencyDisplay amount={total} currency={currency} className="text-4xl" centsClassName="text-xl" />
                  </h1>
                ))}
              </div>
            ) : (
              <h1 className="font-bold tracking-tight">
                <CurrencyDisplay amount={totalBalance} currency={heroCurrency} className="text-4xl" centsClassName="text-xl" />
              </h1>
            )}
            <p className="text-base text-muted-foreground">{t('dashboard.totalBalanceDue')}</p>
          </div>

          <div className="flex flex-wrap justify-center items-start w-full gap-2">
            {showAccountPayment &&
              (payAllBlockedReason ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    {/* A span, because a disabled button fires no pointer events for the tooltip to catch. */}
                    <span className="flex-1 max-w-32">
                      <Button variant="outline" size="default" className="w-full rounded-full" disabled aria-describedby="pay-all-blocked">
                        <GalleryVerticalEnd className="size-4 text-muted-foreground/50 opacity-100!" />
                        {t('navigation.payAll')}
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs" id="pay-all-blocked">
                    <p>{payAllBlockedReason}</p>
                  </TooltipContent>
                </Tooltip>
              ) : (
                <Button variant="outline" size="default" className="flex-1 rounded-full max-w-32" onClick={() => navigate('/account-payment')}>
                  <GalleryVerticalEnd className="size-4 text-muted-foreground/50 opacity-100!" />
                  {t('navigation.payAll')}
                </Button>
              ))}
            {config.authentication?.authMode !== 'none' && (
              <Button
                variant="subtle"
                size="default"
                className="flex-1 rounded-full max-w-32 bg-muted/65 hover:bg-muted"
                onClick={() => navigate('/account')}
              >
                <User className="size-4 text-muted-foreground/75 opacity-100!" />
                {t('navigation.account')}
              </Button>
            )}
            {!!config.ui.companyInfo?.supportUrl && (
              <Button
                variant="subtle"
                size="default"
                className="flex-1 rounded-full max-w-32 bg-muted/65 hover:bg-muted"
                onClick={() => window.open(config.ui.companyInfo?.supportUrl, '_blank')}
              >
                <LifeBuoy className="size-4 text-muted-foreground/75 opacity-100!" />
                {t('navigation.help')}
              </Button>
            )}
            <Button variant="subtle" size="default" className="flex-1 rounded-full max-w-32 bg-muted/65 hover:bg-muted" onClick={handleLogout}>
              {t('navigation.logout')}
              <LogOut className="size-4 text-muted-foreground/75 opacity-100!" />
            </Button>
          </div>

          {/* Debt groups */}
          <div className="space-y-10">
            <DebtGroup
              title={t('dashboard.scheduledPayments')}
              badgeLabel={t('dashboard.scheduled')}
              badgeVariant="info"
              debts={groupedDebts.scheduled}
              onDebtClick={handleDebtClick}
              pendingCaseIds={pendingCaseIds}
            />

            <DebtGroup
              title={t('dashboard.activePaymentPlans')}
              badgeLabel={t('dashboard.inProgress')}
              badgeVariant="warning"
              debts={groupedDebts.inProgress}
              onDebtClick={handleDebtClick}
              pendingCaseIds={pendingCaseIds}
            />

            <DebtGroup
              title={t('dashboard.openDebts')}
              badgeLabel={t('dashboard.unpaid')}
              badgeVariant="subtle"
              debts={groupedDebts.unpaid}
              onDebtClick={handleDebtClick}
              pendingCaseIds={pendingCaseIds}
            />

            <DebtGroup
              title={t('dashboard.clearedDebts')}
              badgeLabel={t('dashboard.done')}
              badgeVariant="success"
              debts={groupedDebts.completed}
              onDebtClick={handleDebtClick}
              pendingCaseIds={pendingCaseIds}
            />

            <RecentPayments debts={debts} />
          </div>
        </div>
      </div>
    </Layout>
  );
}
